import { CtaTargetsPublicResponse } from '@vlt/shared';
import { isAnalyticsAllowed } from './consent';
import type { CtaEventPayload, EventPayload, LeadPayload, Logger, Transport } from './types';

type TransportOptions = {
  apiBase: string;
  logger: Logger;
};

function joinUrl(apiBase: string, path: string): string {
  return `${apiBase.replace(/\/$/, '')}${path}`;
}

function fetchPost(url: string, body: string): Promise<Response> {
  return fetch(url, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body,
    credentials: 'omit',
    keepalive: true,
  });
}

function send(url: string, payload: unknown, logger: Logger): void {
  if (!isAnalyticsAllowed()) return;

  const body = JSON.stringify(payload);
  fetchPost(url, body).catch((error) => logger.warn('transport.fetch_failed', error));
}

export function createTransport({ apiBase, logger }: TransportOptions): Transport {
  const eventsUrl = joinUrl(apiBase, '/api/v1/events');
  const ctaEventsUrl = joinUrl(apiBase, '/api/v1/cta-events');
  const leadsUrl = joinUrl(apiBase, '/api/v1/leads');

  return {
    sendEvent(payload: EventPayload) {
      send(eventsUrl, payload, logger);
    },
    sendCtaEvent(payload: CtaEventPayload) {
      send(ctaEventsUrl, payload, logger);
    },
    async fetchCtaTargets(input) {
      const params = new URLSearchParams({
        tenant: input.tenantId,
        site: input.siteId,
        lp: input.landingPageId,
      });
      const res = await fetch(joinUrl(apiBase, `/api/v1/cta-targets?${params.toString()}`), {
        method: 'GET',
        credentials: 'omit',
      });

      if (!res.ok) {
        throw new Error(`cta targets fetch failed: ${res.status}`);
      }

      const body = await res.json();
      const parsed = CtaTargetsPublicResponse.safeParse(body?.data);
      if (!parsed.success) {
        throw new Error('cta targets response invalid');
      }

      return parsed.data.targets;
    },
    sendLead(payload: LeadPayload) {
      send(leadsUrl, payload, logger);
    },
    async sendLeadAwait(payload: LeadPayload): Promise<Response> {
      if (!isAnalyticsAllowed()) return new Response(null, { status: 204 });

      const body = JSON.stringify(payload);
      return fetchPost(leadsUrl, body);
    },
  };
}
